import type { Game } from '../game/state';
import type { Settings } from '../game/storage';
import { bit } from '../sudoku/types';
import { BOX_OF, COL_OF, ROW_OF } from '../sudoku/units';
import { el } from './dom';

export interface HintHighlight {
  target: number | null;
  because: number[];
}

export interface BoardState {
  selected: number | null;
  hint: HintHighlight | null;
  hidden: boolean;
}

/** Zeichnet das 9x9-Gitter und hält die Zellen aktuell. */
export class BoardView {
  private readonly cells: HTMLButtonElement[] = [];
  private readonly noteNodes: HTMLSpanElement[][] = [];

  constructor(
    private readonly root: HTMLElement,
    private readonly onSelect: (index: number) => void,
  ) {
    for (let i = 0; i < 81; i++) {
      const cell = el('button', {
        class: 'cell',
        type: 'button',
        role: 'gridcell',
        'data-index': String(i),
        'data-row': String(ROW_OF[i]),
        'data-col': String(COL_OF[i]),
      });

      const notes = el('span', { class: 'notes' });
      const spans: HTMLSpanElement[] = [];
      for (let d = 1; d <= 9; d++) {
        const span = el('span', {}, [String(d)]);
        notes.append(span);
        spans.push(span);
      }
      const value = el('span', { class: 'value' });
      cell.append(value, notes);

      this.cells.push(cell);
      this.noteNodes.push(spans);
      this.root.append(cell);
    }

    this.root.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLButtonElement>('.cell');
      if (!target) return;
      this.onSelect(Number(target.dataset.index));
    });
  }

  /** Kurzes Aufblinken, z. B. nach einem Tipp. */
  flash(index: number): void {
    const cell = this.cells[index];
    cell.classList.remove('flash');
    void cell.offsetWidth; // Reflow erzwingen, damit die Animation neu startet
    cell.classList.add('flash');
  }

  focus(index: number): void {
    this.cells[index].focus({ preventScroll: true });
  }

  render(game: Game, state: BoardState, settings: Settings): void {
    const selected = state.selected;
    const selectedValue = selected !== null ? game.values[selected] : 0;

    for (let i = 0; i < 81; i++) {
      const cell = this.cells[i];
      const value = game.values[i];
      const given = game.isGiven(i);
      const valueNode = cell.firstElementChild as HTMLSpanElement;
      const notesNode = cell.lastElementChild as HTMLSpanElement;

      // Inhalt
      if (state.hidden) {
        valueNode.textContent = '';
        notesNode.hidden = true;
      } else if (value !== 0) {
        valueNode.textContent = String(value);
        notesNode.hidden = true;
      } else {
        valueNode.textContent = '';
        notesNode.hidden = false;
        const notes = game.notes[i];
        for (let d = 1; d <= 9; d++) {
          const span = this.noteNodes[i][d - 1];
          span.classList.toggle('on', (notes & bit(d)) !== 0);
          span.classList.toggle('highlight', settings.highlightSameDigit && d === selectedValue);
        }
      }

      // Zustand
      const classes = cell.classList;
      classes.toggle('given', given);
      classes.toggle('wrong', !state.hidden && settings.checkMistakes && game.isWrong(i));
      classes.toggle('selected', selected === i);

      const isPeer =
        selected !== null &&
        selected !== i &&
        (ROW_OF[selected] === ROW_OF[i] || COL_OF[selected] === COL_OF[i] || BOX_OF[selected] === BOX_OF[i]);
      classes.toggle('peer', settings.highlightConflicts && isPeer && !state.hidden);

      classes.toggle(
        'same-digit',
        !state.hidden &&
          settings.highlightSameDigit &&
          selectedValue !== 0 &&
          value === selectedValue &&
          selected !== i,
      );

      classes.toggle('hint-because', !state.hidden && (state.hint?.because.includes(i) ?? false));
      classes.toggle('hint-target', !state.hidden && state.hint?.target === i);

      cell.setAttribute(
        'aria-label',
        `Zeile ${ROW_OF[i] + 1}, Spalte ${COL_OF[i] + 1}: ${value === 0 ? 'leer' : value}${given ? ', vorgegeben' : ''}`,
      );
      cell.disabled = state.hidden;
    }
  }
}
